import React from 'react';
import {
  BarChart3, FileText, Globe, TrendingUp,
  Activity, Shield, Target, Users,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const WindEnergyRepoweringPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Wind Energy Repowering',
      title: (
        <>
          Unlock the full value <em>of wind assets</em>
        </>
      ),
      lede: 'Re-powering of stressed assets to meet PPA limits.',
      image: '/assets/img/general/pinwheels-6535595_1280.jpg',
      imageAlt: 'Wind turbines being assessed for repowering',
      actions: [
        { label: 'Speak with an expert', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '100%', label: 'Portfolio coverage' },
        { value: '24/7', label: 'Monitoring' },
        { value: '50+', label: 'Wind farms' },
        { value: '99.9%', label: 'Compliance rate' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'Extend the life and performance of your wind farms',
      lede: 'Our repowering process delivers the technical, financial, and regulatory outcomes that modern energy portfolios demand — enabling you to exceed contracted capacity and optimize ROI from every megawatt.',
      checklist: [
        'Centralized portfolio management',
        'Streamlined regulatory compliance',
        'Improved operational efficiency',
        'Enhanced decision-making capabilities',
      ],
      image: '/assets/img/general/windmills-1838788_1280.jpg',
      imageAlt: 'Established wind farm approaching repowering',
      imageCaption: 'Operational, regulatory, and financial alignment in one place',
    }}
    process={{
      eyebrow: 'Repowering steps',
      title: 'How a repowering programme runs',
      lede: 'Each stage is validated before the next begins.',
      steps: [
        {
          title: 'Assessment',
          description: 'Comprehensive health audit and technical evaluation of existing assets.',
        },
        {
          title: 'Solution design',
          description: 'Customized plan for technical upgrades and compliance with PPA requirements.',
        },
        {
          title: 'Optimization',
          description: 'Execute performance upgrades, technology refits, and integration.',
        },
        {
          title: 'Validation',
          description: 'Verify output improvement, compliance, and long-term reliability.',
        },
      ],
    }}
    capabilities={{
      eyebrow: 'Portfolio capabilities',
      title: 'Portfolio-level capabilities',
      lede: 'Operational, regulatory, and financial alignment in one place.',
      items: [
        {
          icon: BarChart3,
          title: 'Portfolio overview',
          description: 'Comprehensive view of all wind energy assets across your entire portfolio.',
        },
        {
          icon: FileText,
          title: 'Streamlined reporting',
          description: 'Automated reporting that promotes seamless alignment with regulatory requirements.',
        },
        {
          icon: Globe,
          title: 'Multi-site management',
          description: 'Centralized management of wind farms across different locations and regions.',
        },
        {
          icon: TrendingUp,
          title: 'Performance analytics',
          description: 'Advanced analytics to track and optimize portfolio-wide performance metrics.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Operating model',
      title: 'What the platform handles day to day',
      items: [
        {
          icon: Activity,
          title: 'Real-time monitoring',
          description: 'Monitor all wind assets from a single dashboard.',
        },
        {
          icon: Shield,
          title: 'Regulatory compliance',
          description: 'Automated compliance reporting and documentation.',
        },
        {
          icon: Target,
          title: 'Performance tracking',
          description: 'Track key performance indicators across the portfolio.',
        },
        {
          icon: Users,
          title: 'Resource optimization',
          description: 'Optimize resources and maintenance across all sites.',
        },
      ],
    }}
    cta={{
      title: 'Plan your repowering roadmap',
      body: 'We’ll assess, model, and validate the impact before execution.',
      primary: { label: 'Speak with an expert', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default WindEnergyRepoweringPage;
